import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { StoreExtraDependencies } from "../index";
import type { RootState } from "../index";
import type { SignupFormData } from "@/schemas/signupSchemas";

/**
 * Signup State Status
 */
export type SignupStatus = "idle" | "submitting" | "verifying" | "success" | "error";

/**
 * Signup State Interface
 */
export interface SignupState {
  status: SignupStatus;
  email: string | null;
  error: string | null;
  resendCooldown: number; // Seconds remaining before resend is allowed
  isConfirming: boolean; // True when actively submitting verification code
}

/**
 * Initial State
 */
const initialState: SignupState = {
  status: "idle",
  email: null,
  error: null,
  resendCooldown: 0,
  isConfirming: false,
};

/**
 * Async Thunk: Sign Up User
 *
 * Creates a new account with unified GDPR consent.
 * Maps single gdprConsent checkbox to three clientMetadata values.
 *
 * Uses injected authGateway from store dependencies.
 */
export const signUpAsync = createAsyncThunk<
  { email: string },
  SignupFormData,
  { extra: StoreExtraDependencies; rejectValue: string }
>(
  "signup/signUp",
  async (formData, { extra, rejectWithValue }) => {
    try {
      const { email, password, username, gdprConsent } = formData;

      // Call authGateway.signUp with single params object
      // Note: gdprConsent boolean is converted to clientMetadata inside CognitoAuthGateway
      await extra.authGateway.signUp({
        email,
        password,
        username,
        gdprConsent,
      });

      return { email };
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("signup.errors.unknown");
    }
  }
);

/**
 * Async Thunk: Confirm Email Verification
 *
 * Verifies user email with the code sent by Cognito.
 * Uses injected authGateway from store dependencies.
 */
export const confirmSignUpAsync = createAsyncThunk<
  void,
  { email: string; code: string },
  { extra: StoreExtraDependencies; rejectValue: string }
>(
  "signup/confirmSignUp",
  async ({ email, code }, { extra, rejectWithValue }) => {
    try {
      await extra.authGateway.confirmSignUp(email, code);
    } catch (error) {
      if (error instanceof Error) {
        // Check for specific error types
        if (error.message.includes("expired")) {
          return rejectWithValue("signup.errors.codeExpired");
        }
        if (error.message.includes("Invalid verification code")) {
          return rejectWithValue("signup.errors.invalidCode");
        }
        return rejectWithValue(error.message);
      }
      return rejectWithValue("signup.errors.unknown");
    }
  }
);

/**
 * Async Thunk: Resend Verification Code
 *
 * Resends the email verification code.
 * Uses injected authGateway from store dependencies.
 */
export const resendCodeAsync = createAsyncThunk<
  void,
  string,
  { extra: StoreExtraDependencies; rejectValue: string }
>(
  "signup/resendCode",
  async (email, { extra, rejectWithValue }) => {
    try {
      await extra.authGateway.resendSignUp(email);
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("signup.errors.unknown");
    }
  }
);

/**
 * Signup Slice
 */
const signupSlice = createSlice({
  name: "signup",
  initialState,
  reducers: {
    /**
     * Reset signup state to initial values
     */
    resetSignupState: (state) => {
      state.status = "idle";
      state.email = null;
      state.error = null;
      state.resendCooldown = 0;
      state.isConfirming = false;
    },

    /**
     * Clear error message
     */
    clearSignupError: (state) => {
      state.error = null;
    },

    /**
     * Decrement resend cooldown timer
     */
    decrementCooldown: (state) => {
      if (state.resendCooldown > 0) {
        state.resendCooldown -= 1;
      }
    },
  },
  extraReducers: (builder) => {
    // Sign Up
    builder
      .addCase(signUpAsync.pending, (state) => {
        state.status = "submitting";
        state.error = null;
      })
      .addCase(signUpAsync.fulfilled, (state, action) => {
        state.status = "verifying";
        state.email = action.payload.email;
        state.error = null;
      })
      .addCase(signUpAsync.rejected, (state, action) => {
        state.status = "error";
        state.error = action.payload || "signup.errors.unknown";
        // Check for specific errors
        if (action.payload?.includes("UsernameExistsException")) {
          state.error = "signup.errors.userExists";
        }
        if (action.payload?.includes("password")) {
          state.error = "signup.errors.weakPassword";
        }
      });

    // Confirm Sign Up
    builder
      .addCase(confirmSignUpAsync.pending, (state) => {
        state.status = "verifying";
        state.error = null;
        state.isConfirming = true; // Start confirming
      })
      .addCase(confirmSignUpAsync.fulfilled, (state) => {
        state.status = "success";
        state.error = null;
        // Clear sensitive data after successful verification
        state.email = null;
        state.isConfirming = false; // Done confirming
      })
      .addCase(confirmSignUpAsync.rejected, (state, action) => {
        state.status = "error";
        state.error = action.payload || "signup.errors.invalidCode";
        state.isConfirming = false; // Done confirming (with error)
      });

    // Resend Code
    builder
      .addCase(resendCodeAsync.pending, (state) => {
        state.error = null;
      })
      .addCase(resendCodeAsync.fulfilled, (state) => {
        state.error = null;
        state.resendCooldown = 60; // 60 seconds cooldown
      })
      .addCase(resendCodeAsync.rejected, (state, action) => {
        // Map error to i18n key
        const errorMessage = action.payload || "";
        if (errorMessage.toLowerCase().includes("resend")) {
          state.error = "signup.errors.resendFailed";
        } else {
          state.error = action.payload || "signup.errors.resendFailed";
        }
      });
  },
});

/**
 * Actions
 */
export const { resetSignupState, clearSignupError, decrementCooldown } =
  signupSlice.actions;

/**
 * Selectors
 */
export const selectSignup = (state: RootState) => state.signup;
export const selectSignupStatus = (state: RootState) => state.signup.status;
export const selectSignupEmail = (state: RootState) => state.signup.email;
export const selectSignupError = (state: RootState) => state.signup.error;
export const selectResendCooldown = (state: RootState) =>
  state.signup.resendCooldown;

/**
 * Reducer
 */
export default signupSlice.reducer;
